import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  Output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject } from 'rxjs';
import {
  LucideAngularModule,
  X,
  Mail,
  Lock,
  KeyRound,
  ArrowLeft,
  CheckCircle,
  Eye,
  EyeOff,
} from 'lucide-angular';
import { AuthService } from '../../auth/auth.service';

type ModalStep = 'email' | 'code' | 'success';

@Component({
  selector: 'app-forgot-password-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LucideAngularModule],
  templateUrl: './forgot-password-modal.html',
  styleUrl: './forgot-password-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ForgotPasswordModalComponent {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly isOpen$$ = new BehaviorSubject<boolean>(false);
  protected readonly isOpen$ = this.isOpen$$.asObservable();

  private readonly currentStep$$ = new BehaviorSubject<ModalStep>('email');
  protected readonly currentStep$ = this.currentStep$$.asObservable();

  private readonly isLoading$$ = new BehaviorSubject<boolean>(false);
  protected readonly isLoading$ = this.isLoading$$.asObservable();

  private readonly errorMessage$$ = new BehaviorSubject<string>('');
  protected readonly errorMessage$ = this.errorMessage$$.asObservable();

  protected readonly showPassword = signal(false);
  protected readonly showConfirmPassword = signal(false);

  /** Store email for the reset step */
  private userEmail = '';

  @Input()
  set isOpen(value: boolean) {
    this.isOpen$$.next(value);
    if (value) {
      this.reset();
    }
  }

  @Output() readonly closed = new EventEmitter<void>();

  protected readonly icons = {
    X,
    Mail,
    Lock,
    KeyRound,
    ArrowLeft,
    CheckCircle,
    Eye,
    EyeOff,
  };

  protected readonly emailForm: FormGroup;
  protected readonly resetForm: FormGroup;

  constructor() {
    this.emailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });

    this.resetForm = this.fb.group(
      {
        code: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]],
        newPassword: ['', [Validators.required, Validators.minLength(6)]],
        confirmPassword: ['', [Validators.required]],
      },
      { validators: this.passwordMatchValidator },
    );
  }

  private passwordMatchValidator(form: FormGroup): { [key: string]: boolean } | null {
    const password = form.get('newPassword')?.value;
    const confirmPassword = form.get('confirmPassword')?.value;
    if (password && confirmPassword && password !== confirmPassword) {
      return { passwordMismatch: true };
    }
    return null;
  }

  protected close(): void {
    this.closed.emit();
  }

  protected reset(): void {
    this.currentStep$$.next('email');
    this.emailForm.reset();
    this.resetForm.reset();
    this.errorMessage$$.next('');
    this.isLoading$$.next(false);
    this.userEmail = '';
    this.showPassword.set(false);
    this.showConfirmPassword.set(false);
  }

  protected goBack(): void {
    this.currentStep$$.next('email');
    this.resetForm.reset();
    this.errorMessage$$.next('');
  }

  protected togglePasswordVisibility(): void {
    this.showPassword.update((v) => !v);
  }

  protected toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword.update((v) => !v);
  }

  protected onSubmitEmail(): void {
    if (this.emailForm.invalid) {
      this.emailForm.markAllAsTouched();
      return;
    }

    this.isLoading$$.next(true);
    this.errorMessage$$.next('');

    const email = this.emailForm.get('email')?.value;
    this.userEmail = email;

    this.authService
      .requestPasswordReset({ email })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isLoading$$.next(false);
          this.currentStep$$.next('code');
        },
        error: (error) => {
          this.isLoading$$.next(false);
          this.errorMessage$$.next(
            error.error?.message || 'Failed to send reset code. Please try again.',
          );
        },
      });
  }

  protected onSubmitReset(): void {
    if (this.resetForm.invalid) {
      this.resetForm.markAllAsTouched();
      return;
    }

    this.isLoading$$.next(true);
    this.errorMessage$$.next('');

    const { code, newPassword } = this.resetForm.value;

    this.authService
      .resetPassword({
        email: this.userEmail,
        code,
        newPassword,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isLoading$$.next(false);
          this.currentStep$$.next('success');
        },
        error: (error) => {
          this.isLoading$$.next(false);
          this.errorMessage$$.next(
            error.error?.message || 'Invalid or expired code. Please try again.',
          );
        },
      });
  }

  protected onSuccessClose(): void {
    this.close();
  }
}

